import { Connection, VersionedTransaction, VersionedMessage, TransactionMessage, SystemProgram, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js'
import { HTTP_PROXY_BASE } from './chartUtils'
import { dbgOn, dbg } from './debugFlag'

// ============================================
// Config
// ============================================

const SOL_MINT = 'So11111111111111111111111111111111111111112'

// Jupiter Ultra API - use api.jup.ag with key, lite-api.jup.ag without
const JUP_API_KEY = import.meta.env.VITE_JUP_API_KEY || ''
const ULTRA_BASE = JUP_API_KEY ? 'https://api.jup.ag' : 'https://lite-api.jup.ag'

// QuikNode RPC - only needed for balance lookups now
const RPC_URL = import.meta.env.VITE_RPC_URL || 'https://rpc.cheddarterminal.app/rpc'

const connection = new Connection(RPC_URL, {
  commitment: 'confirmed',
  confirmTransactionInitialTimeout: 90000,
  wsEndpoint: undefined,      // disable auto WebSocket — NolimitNodes HTTP-only from browser
  disableRetryOnRateLimit: false,
})

// ============================================
// Helpers
// ============================================

function getHeaders() {
  const headers = { 'Content-Type': 'application/json' }
  if (JUP_API_KEY) {
    headers['x-api-key'] = JUP_API_KEY
  }
  return headers
}

async function fetchWithRetry(url, options = {}, retries = 3) {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, options)
      if (!response.ok) {
        const errorText = await response.text()
        const e = new Error(`HTTP ${response.status}: ${errorText}`)
        // No route for this pair/amount: retrying returns the same 400.
        if (response.status === 400 && /Failed to get quotes/i.test(errorText)) e.noRetry = true
        throw e
      }
      return response
    } catch (err) {
      if (err.noRetry || i === retries - 1) throw err
      console.warn(`Attempt ${i + 1}/${retries} failed:`, err.message)
      await new Promise(r => setTimeout(r, 1000 * (i + 1)))
    }
  }
}

// ============================================
// Balance functions (still use RPC via QuikNode)
// ============================================

export async function getSOLBalance(walletPubkey) {
  try {
    const balance = await connection.getBalance(walletPubkey)
    return balance / 1e9
  } catch (err) {
    console.error('Error fetching SOL balance:', err)
    return 0
  }
}

export async function getTokenBalance(walletPubkey, tokenMint) {
  try {
    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
      walletPubkey,
      { mint: new PublicKey(tokenMint) }
    )
    if (tokenAccounts.value.length === 0) return 0
    return parseFloat(tokenAccounts.value[0].account.data.parsed.info.tokenAmount.uiAmount) || 0
  } catch (err) {
    // null = UNKNOWN (RPC error), distinct from a confirmed zero balance.
    // Callers that clear positions on zero must skip mutation on null —
    // returning 0 here made every transient RPC failure look like a fully
    // sold position and wiped real holdings from the UI.
    console.error('Error fetching token balance:', err)
    return null
  }
}

export async function getTokenBalanceRaw(walletPubkey, tokenMint) {
  try {
    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
      walletPubkey,
      { mint: new PublicKey(tokenMint) }
    )
    if (tokenAccounts.value.length === 0) return 0
    return parseInt(tokenAccounts.value[0].account.data.parsed.info.tokenAmount.amount) || 0
  } catch (err) {
    console.error('Error fetching token balance:', err)
    return 0
  }
}

// opts.strict: an RPC error THROWS instead of reading as "no tokens". Without
// it, one failed program call (e.g. Token-2022 — every new pump coin) dropped
// those tokens from the list as if the wallet had none, and a failure right
// after a reload (session/RPC not ready yet) emptied the holdings outright.
// WalletHoldings passes strict and keeps its current list on error; other
// callers keep the old lenient behaviour.
export async function getAllTokenHoldings(walletPubkey, opts = {}) {
  const strict = !!opts.strict
  const lenient = (p) => (strict ? p : p.catch(() => ({ value: [] })))
  try {
    const [spl, t22] = await Promise.all([
      lenient(connection.getParsedTokenAccountsByOwner(
        walletPubkey,
        { programId: new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA') }
      )),
      lenient(connection.getParsedTokenAccountsByOwner(
        walletPubkey,
        { programId: new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb') }
      )),
    ])
    const all = [...spl.value, ...t22.value]
    return all
      .map((ta) => {
        const info = ta.account.data.parsed.info
        const uiAmount = parseFloat(info.tokenAmount.uiAmount) || 0
        return {
          mint: info.mint,
          uiAmount,
          decimals: info.tokenAmount.decimals,
          rawAmount: info.tokenAmount.amount,
        }
      })
      .filter((t) => t.uiAmount > 0)
  } catch (err) {
    console.error('Error fetching all token holdings:', err)
    if (strict) throw err
    return []
  }
}

// ============================================
// Ultra API: Get Order
// ============================================

// Market-order slippage (bps) from the user's trade defaults. Sent to Ultra
// as slippageBps (manual mode) — without it Ultra applies its own RTSE
// estimate, which is tight on fast low-caps, and the user's setting (e.g.
// 50%) never reached a market order at all. null = let Ultra decide (RTSE).
let marketSlippageBps = null
export function setMarketSlippageBps(bps) {
  const v = Number.isFinite(bps) && bps > 0 ? Math.round(bps) : null
  if (v === marketSlippageBps) return
  marketSlippageBps = v
  // Cached orders embed the OLD min-out: drop them and rebuild.
  preWarmCache.buy = null
  preWarmCache.sell = null
  if (preWarmConfig) refreshPreWarm()
}

async function getUltraOrder(inputMint, outputMint, amount, taker) {
  const params = new URLSearchParams({
    inputMint,
    outputMint,
    amount: amount.toString(),
    taker: taker.toString(),
  })
  if (marketSlippageBps != null) params.set('slippageBps', String(marketSlippageBps))

  const response = await fetchWithRetry(
    `${ULTRA_BASE}/ultra/v1/order?${params}`,
    { headers: getHeaders() }
  )

  const data = await response.json()

  if (!data.transaction) {
    throw new Error(data.error || 'No transaction returned from Ultra API')
  }

  return data
}

// ============================================
// Ultra API: Execute Order
// ============================================

// Execution outcome errors. `inflight` = the swap was submitted but we never
// got a definitive answer, so it MAY have landed on-chain — callers must verify
// (read balance) instead of assuming failure and risking a double-submit.
// `failed` = a definitive on-chain failure (revert / didn't land).
function inflightError(detail) {
  const e = new Error(`Execution unverified — ${detail}`)
  e.inflight = true
  return e
}
function failedError(msg, signature, code) {
  const e = new Error(`${msg}${code != null ? ` [code ${code}]` : ''}${signature ? ` (${signature})` : ''}`)
  e.failed = true
  e.signature = signature || null
  return e
}

async function executeUltraOrder(signedTransaction, requestId) {
  // The /execute POST is NOT idempotent: Jupiter lands the transaction on its
  // own infra and a reused requestId is rejected. So we deliberately do NOT
  // retry it (unlike /order, a safe GET). A blind retry turns a swap that
  // already landed into a thrown error — strictly worse than the original
  // failure, and the root of phantom fills. Resolve to exactly three states:
  //   • Success  → return data (carries a real signature)
  //   • Failed   → throw failedError. Jupiter confirms server-side, so `status`
  //                already reflects the on-chain result — this IS the meta.err
  //                check; a reverted swap comes back 'Failed'.
  //   • inflight → throw inflightError: no definitive answer (network drop,
  //                gateway error, unparseable body); the tx MAY have landed, so
  //                the caller verifies via balance rather than assuming failure.
  let response
  try {
    response = await fetch(`${ULTRA_BASE}/ultra/v1/execute`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ signedTransaction, requestId }),
    })
  } catch (err) {
    throw inflightError(`network error after submit (${err.message})`)
  }

  let data = null
  try { data = await response.json() } catch {}

  if (data && data.status === 'Success' && data.signature) {
    return data
  }
  if (data && data.status === 'Failed') {
    throw failedError(data.error || 'Swap reverted on-chain', data.signature, data.code)
  }
  // No usable status: ambiguous transport (non-2xx with no body, malformed
  // JSON, gateway timeout). Treat as in-flight — verifying is always safe.
  throw inflightError(`no execution status (HTTP ${response.status})`)
}

// ============================================
// Pre-warm Cache
// ============================================
// Keeps a fresh Ultra order ready so trades skip the ~300ms quote fetch.
// Orders expire after ~25s; cache refreshes every 15s.

const preWarmCache = {
  buy: null,   // { order, tokenMint, amount, wallet, timestamp }
  sell: null,
  balanceCache: new Map(), // mint → { raw, timestamp }
}

// Slippage (min-out) is fixed when the order is BUILT. A 28s-old order on a
// fast low-cap had often moved past its own min-out → 6001 on send. Keep
// cached orders fresh: rebuild every 5s, never use one older than 6s.
const PRE_WARM_MAX_AGE = 6_000
const PRE_WARM_REFRESH = 5_000
const BALANCE_CACHE_MAX_AGE = 30_000 // balance cache: 30s

let preWarmInterval = null
let preWarmConfig = null  // { tokenMint, buyLamports, sellPct, wallet }
let preWarmRefreshing = false  // prevents overlapping concurrent refresh calls

// Fetch and cache a balance — avoids redundant RPC calls on rapid sells
async function getCachedBalanceRaw(walletPubkey, tokenMint) {
  const key = tokenMint
  const cached = preWarmCache.balanceCache.get(key)
  if (cached && Date.now() - cached.timestamp < BALANCE_CACHE_MAX_AGE) {
    dbgOn() && dbg('[PreWarm] Using cached balance:', cached.raw)
    return cached.raw
  }
  const raw = await getTokenBalanceRaw(walletPubkey, tokenMint)
  preWarmCache.balanceCache.set(key, { raw, timestamp: Date.now() })
  return raw
}

// Invalidate balance cache (call after trade executes)
function invalidateBalanceCache(tokenMint) {
  preWarmCache.balanceCache.delete(tokenMint)
}

async function refreshPreWarm() {
  if (!preWarmConfig) return
  if (preWarmRefreshing) return  // already in flight, skip
  preWarmRefreshing = true
  try {
  const { tokenMint, buyLamports, sellPct, wallet } = preWarmConfig

  // Pre-warm buy
  if (buyLamports > 0) {
    try {
      const order = await getUltraOrder(SOL_MINT, tokenMint, buyLamports, wallet)
      preWarmCache.buy = { order, tokenMint, amount: buyLamports, wallet: wallet.toString(), timestamp: Date.now() }
      dbgOn() && dbg('[PreWarm] Buy order cached:', order.inAmount, '→', order.outAmount)
    } catch (err) {
      console.warn('[PreWarm] Buy refresh failed:', err.message)
      preWarmCache.buy = null
    }
  }

  // Small delay between buy and sell fetches to avoid bursting the RPC
  await new Promise(r => setTimeout(r, 1500))

  // Pre-warm sell (need balance first)
  if (sellPct > 0) {
    try {
      const balance = await getCachedBalanceRaw(wallet, tokenMint)
      if (balance > 0) {
        const sellAmount = Math.floor(balance * (sellPct / 100))
        // Dust: tiny raw amount, or worth < ~0.0001 SOL at the buy pre-warm's rate.
        const _b = preWarmCache.buy
        const _lpr = (_b && _b.tokenMint === tokenMint && Number(_b.order?.outAmount) > 0) ? Number(_b.order.inAmount) / Number(_b.order.outAmount) : null
        const _dust = sellAmount < 1000 || (_lpr != null && sellAmount * _lpr < 100_000)
        if (_dust) preWarmCache.sell = null
        else if (sellAmount > 0) {
          const order = await getUltraOrder(tokenMint, SOL_MINT, sellAmount, wallet)
          preWarmCache.sell = { order, tokenMint, amount: sellAmount, wallet: wallet.toString(), timestamp: Date.now() }
          dbgOn() && dbg('[PreWarm] Sell order cached:', order.inAmount, '→', order.outAmount)
        }
      }
    } catch (err) {
      console.warn('[PreWarm] Sell refresh failed:', err.message)
      preWarmCache.sell = null
    }
  }
  } finally {
    preWarmRefreshing = false
  }
}

// Start pre-warming for a token + wallet combo
export function startPreWarm(tokenMint, buyAmountSol, sellPercentage, wallet) {
  if (!tokenMint || !wallet) return

  // Skip restart if config hasn't changed — effect fires multiple times during React init
  const newLamports = Math.floor(buyAmountSol * 1e9)
  const walletStr = wallet.toString()
  if (
    preWarmConfig &&
    preWarmInterval &&
    preWarmConfig.tokenMint === tokenMint &&
    preWarmConfig.buyLamports === newLamports &&
    preWarmConfig.sellPct === sellPercentage &&
    preWarmConfig.walletStr === walletStr
  ) {
    return  // already running with identical config
  }

  stopPreWarm()

  preWarmConfig = {
    tokenMint,
    buyLamports: newLamports,
    sellPct: sellPercentage,
    wallet,          // keep original PublicKey object — getTokenBalanceRaw needs .toBase58()
    walletStr,       // string copy for dedup comparisons
  }

  // Immediate first fetch
  refreshPreWarm()

  // Refresh on interval
  preWarmInterval = setInterval(refreshPreWarm, PRE_WARM_REFRESH)
  dbg('[PreWarm] Started for', tokenMint.slice(0, 8), '— buy:', buyAmountSol, 'SOL, sell:', sellPercentage, '%')
}

export function stopPreWarm() {
  if (preWarmInterval) {
    clearInterval(preWarmInterval)
    preWarmInterval = null
  }
  preWarmCache.buy = null
  preWarmCache.sell = null
  preWarmConfig = null
}

// Try to use cached order; returns null if stale/mismatched
function getCachedOrder(side, tokenMint, amount, wallet) {
  const cached = preWarmCache[side]
  if (!cached) return null
  if (Date.now() - cached.timestamp > PRE_WARM_MAX_AGE) return null
  if (cached.tokenMint !== tokenMint) return null
  if (cached.wallet !== wallet.toString()) return null
  // For buys, amount must match exactly
  if (side === 'buy' && cached.amount !== amount) return null
  // For sells, allow small variance (balance might shift slightly)
  if (side === 'sell') {
    const ratio = amount / cached.amount
    if (ratio < 0.95 || ratio > 1.05) return null
  }
  dbgOn() && dbg(`[PreWarm] Using cached ${side} order (${Date.now() - cached.timestamp}ms old)`)
  return cached.order
}

// ============================================
// Execute Buy (SOL -> Token) — with pre-warm
// ============================================

export async function executeBuy(tokenMint, solAmount, wallet, signTransaction, onSent) {
  const lamports = Math.floor(solAmount * 1e9)

  // Try cached order first
  let order = getCachedOrder('buy', tokenMint, lamports, wallet)
  if (!order) {
    order = await getUltraOrder(SOL_MINT, tokenMint, lamports, wallet)
  }
  dbg('[Ultra] Buy order:', order.inAmount, '→', order.outAmount)

  const txBuf = Buffer.from(order.transaction, 'base64')
  const transaction = VersionedTransaction.deserialize(txBuf)
  const signedTx = await signTransaction(transaction)
  const signedBase64 = Buffer.from(signedTx.serialize()).toString('base64')

  // Invalidate caches — order is used, balance will change
  preWarmCache.buy = null
  invalidateBalanceCache(tokenMint)

  // Fire-and-forget: notify caller immediately after submission
  if (onSent) onSent()

  const result = await executeUltraOrder(signedBase64, order.requestId)
  dbg('[Ultra] Buy executed:', result)

  // Trigger background re-warm
  setTimeout(refreshPreWarm, 2000)

  // Return both txid and order amounts for position tracking.
  // result.signature is guaranteed by executeUltraOrder's Success gate — the
  // old `|| 'success'` fallback polluted tracking with an unresolvable txid.
  return {
    txid: result.signature,
    outAmount: order.outAmount,
    inAmount: order.inAmount,
  }
}

// ============================================
// Execute Sell (Token -> SOL) — with pre-warm + cached balance
// ============================================

export async function executeSell(tokenMint, percentage, wallet, signTransaction, onSent) {
  // Use cached balance to skip RPC round trip
  const balance = await getCachedBalanceRaw(wallet, tokenMint)

  if (balance === 0) {
    throw new Error('No tokens to sell')
  }

  const sellAmount = Math.floor(balance * (percentage / 100))

  if (sellAmount === 0) {
    throw new Error('Sell amount too small')
  }

  // Try cached order first
  let order = getCachedOrder('sell', tokenMint, sellAmount, wallet)
  if (!order) {
    order = await getUltraOrder(tokenMint, SOL_MINT, sellAmount, wallet)
  }
  dbg('[Ultra] Sell order:', order.inAmount, '→', order.outAmount)

  const txBuf = Buffer.from(order.transaction, 'base64')
  const transaction = VersionedTransaction.deserialize(txBuf)
  const signedTx = await signTransaction(transaction)
  const signedBase64 = Buffer.from(signedTx.serialize()).toString('base64')

  // Invalidate caches
  preWarmCache.sell = null
  invalidateBalanceCache(tokenMint)

  // Fire-and-forget: notify caller immediately after submission
  if (onSent) onSent()

  const result = await executeUltraOrder(signedBase64, order.requestId)
  dbg('[Ultra] Sell executed:', result)

  // Trigger background re-warm
  setTimeout(refreshPreWarm, 2000)

  // Return both txid and order amounts for consistency with executeBuy.
  // result.signature is guaranteed by executeUltraOrder's Success gate.
  return {
    txid: result.signature,
    outAmount: order.outAmount,
    inAmount: order.inAmount,
  }
}

// ============================================
// Native SOL transfer
// ============================================
// Send SOL to an arbitrary address. NOT a Jupiter swap — no Ultra order, just a
// SystemProgram.transfer in a v0 tx, signed through the SAME signTransaction the
// buy/sell path uses (so it works for burner, browser and Privy alike). Uses the
// module-private `connection` from inside jupiter.js, which is exactly why this
// lives here instead of the popover reaching for a connection it can't have.
//
// Guards up front rather than trusting the caller: an arbitrary-recipient
// transfer is the one action where a bad input costs real money and can't be
// clawed back.
export async function sendSol(recipientAddress, solAmount, wallet, signTransaction) {
  if (!wallet) throw new Error('No wallet connected')
  if (!signTransaction) throw new Error('Wallet cannot sign')

  // Recipient must be a real pubkey. new PublicKey throws on garbage; catch it
  // and say something useful instead of leaking the base58 error.
  let toPubkey
  try { toPubkey = new PublicKey(String(recipientAddress).trim()) }
  catch { throw new Error('Invalid recipient address') }

  const fromPubkey = new PublicKey(wallet)
  if (toPubkey.equals(fromPubkey)) throw new Error('Recipient is your own wallet')

  const amount = Number(solAmount)
  if (!(amount > 0)) throw new Error('Amount must be greater than 0')

  const lamports = Math.round(amount * LAMPORTS_PER_SOL)
  if (lamports <= 0) throw new Error('Amount too small')

  // Rent math, for real this time. A system account must stay RENT-EXEMPT
  // (~890,880 lamports for 0 bytes) or go to exactly zero — the runtime REJECTS
  // a transfer that leaves the sender with a nonzero dust balance below the
  // line. The first version's buffer was fee-only, so its own "max sendable"
  // suggestion constructed a guaranteed-fail transaction. Reserve rent + fee.
  const RENT_EXEMPT_MIN = 890_880
  const FEE_BUFFER = 5000 + 5000   // signature fee + headroom
  const bal = await connection.getBalance(fromPubkey, 'confirmed')
  if (lamports + FEE_BUFFER + RENT_EXEMPT_MIN > bal) {
    const maxSol = Math.max(0, (bal - FEE_BUFFER - RENT_EXEMPT_MIN) / LAMPORTS_PER_SOL)
    throw new Error(`Insufficient SOL — max sendable ≈ ${maxSol.toFixed(4)} (rent + fee stay behind)`)
  }
  // Same rule on the RECEIVING side: funding a brand-new account below the
  // rent-exempt minimum fails at the runtime with an error nobody can read.
  // Only worth the extra RPC when the amount is actually below the line.
  if (lamports < RENT_EXEMPT_MIN) {
    const rBal = await connection.getBalance(toPubkey, 'confirmed').catch(() => 0)
    if (!rBal) throw new Error('Amount too small to open a new wallet — send at least 0.0009 SOL')
  }

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
  const msg = new TransactionMessage({
    payerKey: fromPubkey,
    recentBlockhash: blockhash,
    instructions: [SystemProgram.transfer({ fromPubkey, toPubkey, lamports })],
  }).compileToV0Message()

  const tx = new VersionedTransaction(msg)
  const signed = await signTransaction(tx)

  const sig = await connection.sendRawTransaction(signed.serialize(), {
    skipPreflight: false,
    maxRetries: 3,
  })
  // Confirm so the UI can report done rather than "submitted, good luck".
  await connection.confirmTransaction({ signature: sig, blockhash, lastValidBlockHeight }, 'confirmed')

  // Balance just changed — drop the cache so the next read is fresh.
  try { invalidateBalanceCache(SOL_MINT) } catch {}

  return { txid: sig, lamports }
}

// ============================================
// Trigger API: Limit Orders
// ============================================

// ============================================================
// TRIGGER V2 — stage 1 scaffold (auth + flag architecture)
// ------------------------------------------------------------
// V2 is a TRUST-MODEL change, not a path swap: orders fund a Privy-managed
// custodial vault (one per wallet), triggers are USD-price based, fills are
// market-routed (output NOT guaranteed), and every endpoint requires an API
// key (`x-api-key`) — the keyless lite-api fallback does not exist for V2.
// Authenticated endpoints additionally take `Authorization: Bearer <jwt>`.
// V1 stays fully intact below; NOTHING dispatches to V2 until BOTH gates
// pass: the user flag (localStorage cheddar_trigger_v2 = '1') AND
// TRIGGER_V2_READY, which stays false until every schema in the fence is
// filled from live docs and proven with real money. Flag-on before that
// logs once and falls through to V1 — a half-built path can never execute.
//
// VERIFIED against docs (Aug 2026):
//   base   https://api.jup.ag/trigger/v2
//   auth   POST /auth/challenge  { walletPubkey, type:'message' }
//          -> { type, challenge }   ('transaction' variant for hw wallets)
//   flow   authenticate -> resolve/register vault -> craft+sign deposit
//          (deposit now requires order metadata — recent breaking change)
//          -> create order; price orders editable in place; $10 min.
// FENCED (V2_SCHEMAS): auth verify -> JWT, vault resolve/register, deposit
// craft, createOrder/getOrders/cancel bodies. Doc URLs sit on each entry.
// ============================================================
const TRIGGER_V2_BASE = 'https://api.jup.ag/trigger/v2'
export const TRIGGER_V2_READY = false   // flips only when V2_SCHEMAS is complete + live-tested
export const triggerV2Enabled = () => {
  try { return localStorage.getItem('cheddar_trigger_v2') === '1' } catch { return false }
}
const requireV2Key = () => {
  if (!JUP_API_KEY) throw new Error('Trigger V2 requires a Jupiter API key (VITE_JUP_API_KEY) — no keyless fallback exists for V2')
}
const v2Fetch = async (path, { jwt = null, method = 'POST', body = null } = {}) => {
  requireV2Key()
  const headers = { 'Content-Type': 'application/json', 'x-api-key': JUP_API_KEY }
  if (jwt) headers['Authorization'] = `Bearer ${jwt}`
  const res = await fetch(`${TRIGGER_V2_BASE}${path}`, {
    method, headers, body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => null)
  if (!res.ok) throw new Error(`Trigger V2 ${path} ${res.status}: ${data?.error || data?.message || 'request failed'}`)
  return data
}
// Fill each from its doc page, then flip TRIGGER_V2_READY. Every entry is a
// pure payload/path description — the plumbing above is already done.
const V2_SCHEMAS = {
  // developers.jup.ag/docs/trigger/authentication — signed challenge -> JWT
  // (24h validity per docs; the 20-min cache below stays conservative).
  authVerify: async (fetch2, { walletPubkey, challenge, sigBytes }) => {
    const sig = (typeof Buffer !== 'undefined')
      ? Buffer.from(sigBytes).toString('base64')
      : btoa(String.fromCharCode(...sigBytes))
    const res = await fetch2('/auth/verify', {
      body: { walletPubkey, type: 'message', challenge, signature: sig },
    })
    const jwt = res?.token || res?.jwt
    if (!jwt) throw new Error('Trigger V2 auth: no token in /auth/verify response')
    return jwt
  },

  // developers.jup.ag/docs/trigger/deposit — one vault per wallet;
  // GET /vault resolves, GET /vault/register creates on first use.
  vaultResolve: async (fetch2, { jwt }) => {
    const opts = { method: 'GET', jwt }
    try {
      const v = await fetch2('/vault', opts)
      if (v && !v.error) return v
    } catch {}
    return fetch2('/vault/register', opts)
  },

  // developers.jup.ag/docs/trigger/deposit — craft the unsigned deposit tx.
  // The "recent breaking change" the fence comment predicted IS the order
  // metadata: orderType ('price' | 'dca') + orderSubType
  // ('single' | 'oco' | 'otoco') are REQUIRED on the craft. Min 10 USD.
  depositCraft: async (fetch2, { jwt, inputMint, outputMint, amount, orderType = 'price', orderSubType = 'single' }) => {
    const res = await fetch2('/deposit/craft', {
      jwt,
      body: { inputMint, outputMint, amount: String(amount), orderType, orderSubType },
    })
    if (!res?.transaction) throw new Error('Trigger V2 deposit: no transaction from /deposit/craft')
    return res   // { transaction, requestId, ... } — sign, then carry both into createOrder
  },

  // developers.jup.ag/docs/trigger/create-order — POST /orders/price.
  // subType single | oco | otoco; expiresAt REQUIRED; trailing stops use
  // trailingBps (50-9000) INSTEAD of triggerPriceUsd; oco/otoco carry legs.
  createOrder: async (fetch2, {
    jwt, requestId, depositSignedTx, inputMint, outputMint, amount,
    triggerPriceUsd, trailingBps, slippageBps, expiresAt, subType = 'single', legs,
  }) => {
    if (!expiresAt) throw new Error('Trigger V2: expiresAt is required')
    if (trailingBps == null && triggerPriceUsd == null) throw new Error('Trigger V2: need triggerPriceUsd or trailingBps')
    const body = {
      requestId, depositSignedTx, inputMint, outputMint,
      orderSubType: subType, expiresAt,
    }
    if (amount != null) body.amount = String(amount)
    if (trailingBps != null) body.trailingBps = Math.max(50, Math.min(9000, Math.round(trailingBps)))
    else body.triggerPriceUsd = String(triggerPriceUsd)
    if (slippageBps != null) body.slippageBps = String(slippageBps)
    if (legs) body.legs = legs
    return fetch2('/orders/price', { jwt, body })
  },

  // developers.jup.ag/docs/trigger/manage-orders —
  //   update: PATCH /orders/price/{id} (trigger price / slippage in place)
  //   cancel: two-step — POST cancel/{id} moves it to ready_to_cancel (no
  //   further fills), sign the returned WITHDRAWAL tx, then confirm-cancel.
  //   Expired-order withdrawals ride the same two-step.
  orders: {
    update: async (fetch2, { jwt, orderId, patch }) =>
      fetch2(`/orders/price/${orderId}`, { method: 'PATCH', jwt, body: patch }),
    cancel: async (fetch2, { jwt, orderId, signTx }) => {
      const d1 = await fetch2(`/orders/price/cancel/${orderId}`, { jwt, body: {} })
      if (!d1?.transaction) return d1            // nothing to withdraw — already settled
      const signed = await signTx(d1.transaction)
      return fetch2(`/orders/price/confirm-cancel/${orderId}`, {
        jwt, body: { signedTransaction: signed, requestId: d1.requestId },
      })
    },
  },
}
// JWT cache per wallet. Expiry semantics come with the verify schema; until
// then a conservative 20-minute staleness window forces re-auth, and any 401
// downstream should clear the entry and retry once.
const _v2Jwt = new Map()
export async function authenticateTriggerV2(walletPubkey, signMessage) {
  requireV2Key()
  const key = walletPubkey.toString()
  const cached = _v2Jwt.get(key)
  if (cached && Date.now() - cached.at < 20 * 60_000) return cached.jwt
  // VERIFIED: challenge request/response shape is exact.
  const { challenge } = await v2Fetch('/auth/challenge', {
    body: { walletPubkey: key, type: 'message' },
  })
  if (!challenge) throw new Error('Trigger V2 auth: empty challenge')
  const sigBytes = await signMessage(new TextEncoder().encode(challenge))
  if (!V2_SCHEMAS.authVerify) {
    throw new Error('Trigger V2 auth: verify schema pending (V2_SCHEMAS.authVerify) — see developers.jup.ag/docs/trigger/authentication')
  }
  const jwt = await V2_SCHEMAS.authVerify(v2Fetch, { walletPubkey: key, challenge, sigBytes })
  _v2Jwt.set(key, { jwt, at: Date.now() })
  return jwt
}
// Dispatch guard used by the V1 exports once stages 3-4 land: both gates,
// with a one-time console note when the flag is set but the build isn't.
let _v2NotedNotReady = false
export function useTriggerV2() {
  const on = triggerV2Enabled()
  if (on && !TRIGGER_V2_READY && !_v2NotedNotReady) {
    _v2NotedNotReady = true
    console.warn('[TriggerV2] flag is set but this build is not schema-complete — using V1')
  }
  return on && TRIGGER_V2_READY
}

const TRIGGER_BASE = ULTRA_BASE  // Same base — lite-api.jup.ag (free) or api.jup.ag (keyed)

const PUMP_TOTAL_SUPPLY = 1_000_000_000

// Real mint supply + decimals (orders-venue-20261016). The old math assumed
// every coin was a pump.fun mint (1B supply, 6 decimals), which mispriced
// limit orders on anything else. Cached per mint for 5 min; supply barely
// moves and decimals never do. Throws rather than guessing.
const _supplyCache = new Map()   // mint -> { at, supplyUi, decimals }
export async function getMintSupplyInfo(tokenMint) {
  const hit = _supplyCache.get(tokenMint)
  if (hit && Date.now() - hit.at < 300_000) return hit
  const { value } = await connection.getTokenSupply(new PublicKey(tokenMint))
  const decimals = Number(value?.decimals)
  const supplyUi = Number(value?.amount) / 10 ** decimals
  if (!Number.isFinite(decimals) || !(supplyUi > 0)) throw new Error('Could not read token supply for limit price')
  const out = { at: Date.now(), supplyUi, decimals }
  _supplyCache.set(tokenMint, out)
  return out
}

// Convert target mcap (SOL) to lamports per raw token unit
async function mcapToLamportsPerRawToken(tokenMint, targetMcapSol) {
  const { supplyUi, decimals } = await getMintSupplyInfo(tokenMint)
  const pricePerTokenSol = targetMcapSol / supplyUi
  return pricePerTokenSol * 1e9 / 10 ** decimals  // lamports per raw token unit
}

async function createTriggerOrder(inputMint, outputMint, maker, makingAmount, takingAmount, options = {}) {
  const body = {
    inputMint,
    outputMint,
    maker: maker.toString(),
    payer: maker.toString(),
    params: {
      makingAmount: makingAmount.toString(),
      takingAmount: takingAmount.toString(),
    },
    computeUnitPrice: 'auto',
  }

  // V2 optional params
  if (options.slippageBps) body.params.slippageBps = options.slippageBps.toString()
  if (options.expiredAt) body.params.expiredAt = options.expiredAt.toString()

  const response = await fetchWithRetry(
    `${TRIGGER_BASE}/trigger/v1/createOrder`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
    }
  )
  const data = await response.json()
  if (data.error) throw new Error(data.error + (data.cause ? `: ${data.cause}` : ''))
  if (!data.transaction) throw new Error('No transaction returned from Trigger API')
  return data
}

async function executeTriggerOrder(signedTransaction, requestId) {
  const response = await fetchWithRetry(
    `${TRIGGER_BASE}/trigger/v1/execute`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ signedTransaction, requestId }),
    }
  )
  const data = await response.json()
  if (data.error) throw new Error(data.error)
  return data
}

// Fetch open trigger orders for a wallet
export async function getOpenTriggerOrders(wallet) {
  const res = await fetch(
    `${TRIGGER_BASE}/trigger/v1/getTriggerOrders?user=${wallet.toString()}&orderStatus=active`,
    { headers: getHeaders() }
  )
  const data = await res.json()
  return data.orders || []
}

// Past (filled / cancelled / expired) trigger orders — used to tell a FILL
// from a cancel/expiry when an order drops out of the active list.
// Throws on HTTP error so the caller can surface it.
export async function getTriggerOrderHistory(wallet) {
  const res = await fetch(
    `${TRIGGER_BASE}/trigger/v1/getTriggerOrders?user=${wallet.toString()}&orderStatus=history`,
    { headers: getHeaders() }
  )
  if (!res.ok) throw new Error(`Trigger history HTTP ${res.status}`)
  const data = await res.json()
  return data.orders || []
}

/** 'filled' | 'partial' | 'cancelled' | 'expired' | 'unknown' for a v1 history row. */
export function classifyTriggerOrder(o) {
  const st = String(o?.status || o?.orderStatus || '').toLowerCase()
  const trades = Array.isArray(o?.trades) ? o.trades.length : 0
  if (st.includes('complet') || st.includes('fill')) return 'filled'
  if (st.includes('expir')) return trades ? 'partial' : 'expired'
  if (st.includes('cancel')) return trades ? 'partial' : 'cancelled'
  return trades ? 'filled' : 'unknown'
}

// Cancel a trigger order
export async function cancelTriggerOrder(orderId, wallet, signTransaction) {
  const response = await fetchWithRetry(
    `${TRIGGER_BASE}/trigger/v1/cancelOrder`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        maker: wallet.toString(),
        computeUnitPrice: 'auto',
        order: orderId,
      }),
    }
  )
  const data = await response.json()
  if (data.error) throw new Error(data.error)

  const txBuf = Buffer.from(data.transaction, 'base64')
  const transaction = VersionedTransaction.deserialize(txBuf)
  const signedTx = await signTransaction(transaction)
  const signedBase64 = Buffer.from(signedTx.serialize()).toString('base64')

  const result = await executeTriggerOrder(signedBase64, data.requestId)
  return result
}

// Limit Buy: spend X SOL to buy tokens when mcap hits target (SOL-denominated)
export async function executeLimitBuy(tokenMint, solAmount, targetMcapSol, wallet, signTransaction, options = {}) {
  const lamportsPerRawToken = await mcapToLamportsPerRawToken(tokenMint, targetMcapSol)
  const makingAmount = Math.floor(solAmount * 1e9)
  const takingAmount = Math.floor(makingAmount / lamportsPerRawToken)

  dbg(`[Trigger] Limit buy: ${solAmount} SOL for ${takingAmount} raw tokens at ${targetMcapSol.toFixed(0)} SOL mcap`)
  dbg(`[Trigger] making=${makingAmount} taking=${takingAmount}`)

  const order = await createTriggerOrder(SOL_MINT, tokenMint, wallet, makingAmount, takingAmount, options)

  const txBuf = Buffer.from(order.transaction, 'base64')
  const transaction = VersionedTransaction.deserialize(txBuf)
  const signedTx = await signTransaction(transaction)
  const signedBase64 = Buffer.from(signedTx.serialize()).toString('base64')

  const result = await executeTriggerOrder(signedBase64, order.requestId)
  dbg('[Trigger] Limit buy placed:', result)

  return { orderId: order.order, signature: result.signature || result.transactionId || 'success' }
}

// Limit Sell: sell X% of tokens when mcap hits target (SOL-denominated)
export async function executeLimitSell(tokenMint, percentage, targetMcapSol, wallet, signTransaction, options = {}) {
  const lamportsPerRawToken = await mcapToLamportsPerRawToken(tokenMint, targetMcapSol)
  // options.rawAmount: exact size (used when MOVING an order so the re-placed
  // order matches the original instead of re-reading a lagging balance).
  let makingAmount
  if (options.rawAmount > 0) {
    makingAmount = Math.floor(options.rawAmount)
  } else {
    const rawBalance = await getTokenBalanceRaw(wallet, tokenMint)
    if (rawBalance === 0) throw new Error('No tokens to sell')
    makingAmount = Math.floor(rawBalance * (percentage / 100))
  }
  if (makingAmount === 0) throw new Error('Sell amount too small')

  const takingAmount = Math.floor(makingAmount * lamportsPerRawToken)

  dbg(`[Trigger] Limit sell: ${makingAmount} raw tokens for ${takingAmount / 1e9} SOL at ${targetMcapSol.toFixed(0)} SOL mcap`)
  dbg(`[Trigger] making=${makingAmount} taking=${takingAmount}`)

  const order = await createTriggerOrder(tokenMint, SOL_MINT, wallet, makingAmount, takingAmount, options)

  const txBuf = Buffer.from(order.transaction, 'base64')
  const transaction = VersionedTransaction.deserialize(txBuf)
  const signedTx = await signTransaction(transaction)
  const signedBase64 = Buffer.from(signedTx.serialize()).toString('base64')

  const result = await executeTriggerOrder(signedBase64, order.requestId)
  dbg('[Trigger] Limit sell placed:', result)

  return { orderId: order.order, signature: result.signature || result.transactionId || 'success', rawAmount: makingAmount }
}

// ============================================
// Cancel Multiple Orders
// ============================================

export async function cancelMultipleTriggerOrders(orderIds, wallet, signTransaction) {
  const results = []
  for (const orderId of orderIds) {
    try {
      const result = await cancelTriggerOrder(orderId, wallet, signTransaction)
      results.push({ orderId, success: true, result })
    } catch (err) {
      console.warn(`[Trigger] Failed to cancel order ${orderId}:`, err.message)
      results.push({ orderId, success: false, error: err.message })
    }
  }
  return results
}

// ============================================
// OCO: One-Cancels-Other (TP + SL)
// ============================================

// Place a take-profit and stop-loss simultaneously, returns both order IDs
export async function executeOCO(tokenMint, tpPercentage, tpMcapSol, slPercentage, slMcapSol, wallet, signTransaction, options = {}) {
  dbg(`[OCO] Placing TP at ${tpMcapSol.toFixed(0)} SOL mcap (${tpPercentage}%) + SL at ${slMcapSol.toFixed(0)} SOL mcap (${slPercentage}%)`)

  // Place take-profit (sell at higher mcap)
  const tpResult = await executeLimitSell(tokenMint, tpPercentage, tpMcapSol, wallet, signTransaction, options)
  dbg('[OCO] TP order placed:', tpResult.orderId)

  // Stop-loss is NOT placed here (orders-venue-20261016). Trigger v1 is
  // buy-below / sell-above only: a sell limit BELOW price fills instantly,
  // so the old SL leg dumped the bag the moment the OCO was placed. The
  // caller (useTrading) arms a client-side stop for the SL leg instead.
  return {
    tpOrderId: tpResult.orderId,
    slOrderId: null,
    tpSignature: tpResult.signature,
    slSignature: null,
  }
}

// Poll OCO orders — when one fills, cancel the other
// Returns a cleanup function to stop polling
export function startOCOMonitor(tpOrderId, slOrderId, wallet, signTransaction, onUpdate) {
  let stopped = false
  const POLL_INTERVAL = 10_000 // 10 seconds

  const poll = async () => {
    while (!stopped) {
      try {
        const orders = await getOpenTriggerOrders(wallet)
        const tpActive = orders.some(o => o.publicKey === tpOrderId || o.orderKey === tpOrderId || o.account?.orderKey === tpOrderId)
        const slActive = orders.some(o => o.publicKey === slOrderId || o.orderKey === slOrderId || o.account?.orderKey === slOrderId)

        if (!tpActive && slActive) {
          // TP filled — cancel SL
          dbg('[OCO] TP filled, cancelling SL...')
          try {
            await cancelTriggerOrder(slOrderId, wallet, signTransaction)
            onUpdate?.({ type: 'tp_filled', cancelled: 'sl' })
          } catch (err) {
            console.warn('[OCO] Failed to cancel SL:', err.message)
            onUpdate?.({ type: 'tp_filled', cancelError: err.message })
          }
          return
        }

        if (!slActive && tpActive) {
          // SL filled — cancel TP
          dbg('[OCO] SL filled, cancelling TP...')
          try {
            await cancelTriggerOrder(tpOrderId, wallet, signTransaction)
            onUpdate?.({ type: 'sl_filled', cancelled: 'tp' })
          } catch (err) {
            console.warn('[OCO] Failed to cancel TP:', err.message)
            onUpdate?.({ type: 'sl_filled', cancelError: err.message })
          }
          return
        }

        if (!tpActive && !slActive) {
          // Both gone (both cancelled externally or both filled)
          dbg('[OCO] Both orders no longer active')
          onUpdate?.({ type: 'both_gone' })
          return
        }

        // Both still active — keep polling
        onUpdate?.({ type: 'polling', tpActive, slActive })
      } catch (err) {
        console.warn('[OCO] Poll error:', err.message)
      }

      await new Promise(r => setTimeout(r, POLL_INTERVAL))
    }
  }

  poll()

  return () => { stopped = true }
}

// ============================================
// Token name lookup
// ============================================

// ============================================
// Token Search (Jupiter v2)
// ============================================

// Search Jupiter's token list by name/symbol/mint. Returns normalized
// {id, symbol, name, icon, fdv, usdPrice, organicScore, verified} objects.

// ─── Token image URL normalizer ───
// Token metadata points at whatever gateway the launchpad happened to use, and
// several of those fail for a large slice of users:
//   * cf-ipfs.com — deprecated; also DNS-blocked by filtering resolvers and
//     some browsers' built-in VPN/blocker layers (ERR_NAME_NOT_RESOLVED).
//   * <name>.mypinata.cloud — DEDICATED gateways, usually locked to their
//     owner's referer, so hotlinking from Cheddar returns 403.
// Both are content-addressed, so the CID is portable: rewrite any recognizable
// /ipfs/<cid> (or ipfs:// URI) onto a neutral public gateway and the same bytes
// load. Non-IPFS URLs pass through untouched — this normalizes, it never
// guesses. Applied at the one place tokens enter the app, so every consumer
// (cards, watchlist, ticker, chart chip) heals at once.
// Now points at OUR OWN origin, not a public gateway. Every named gateway has
// eventually failed in a browser-specific way — cf-ipfs.com went DNS-dead,
// pinata dedicated gateways 403 on referer, and ipfs.io started blocking
// cross-origin embedding (NotSameOrigin). Those are all BROWSER-side policies;
// a server-side fetch sees none of them. /api/ipfs/<cid> is a Pages Function
// that walks a gateway chain and edge-caches the bytes (see
// functions/api/ipfs/[[path]].js), so one dead gateway is now a retry instead
// of a blank icon.
const IPFS_PROXY = '/api/ipfs/'

// Rewrite EVERY recognizable IPFS URL, not just the known-flaky hosts. The old
// allow-list approach meant each newly-broken gateway needed a code change and
// a deploy; a CID is portable by definition, so routing all of them through one
// door ends that game. Non-IPFS URLs still pass through untouched.
export function normalizeTokenImage(url) {
  if (!url || typeof url !== 'string') return ''
  try {
    if (url.startsWith('ipfs://')) return IPFS_PROXY + url.slice(7).replace(/^ipfs\//, '')
    const u = new URL(url)
    const m = u.pathname.match(/^\/ipfs\/(.+)$/)
    if (!m) return url
    return IPFS_PROXY + m[1] + u.search
  } catch { return url }
}
// Pass an AbortSignal so rapid typing can cancel in-flight requests instead
// of letting stale results overwrite fresh ones when they resolve late.
//
// query: user-typed string (symbol, name, or mint address)
// signal: AbortSignal (optional)
// limit: max results to return (default 10)
export async function searchTokens(query, { signal, limit = 10 } = {}) {
  const q = (query || '').trim()
  if (!q) return []
  const url = `${ULTRA_BASE}/tokens/v2/search?query=${encodeURIComponent(q)}`
  try {
    const res = await fetch(url, { headers: getHeaders(), signal })
    if (!res.ok) return []
    const data = await res.json()
    const arr = Array.isArray(data) ? data : []
    // Jupiter ranks results server-side; just trim to limit. Map to a
    // stable shape — Jupiter sometimes returns nested mintAuthority/
    // freezeAuthority/audit objects we don't need at the UI layer.
    return arr.slice(0, limit).map(t => ({
      id: t.id,
      symbol: t.symbol || '',
      name: t.name || '',
      icon: normalizeTokenImage(t.icon),
      fdv: typeof t.fdv === 'number' ? t.fdv : null,
      usdPrice: typeof t.usdPrice === 'number' ? t.usdPrice : null,
      organicScore: typeof t.organicScore === 'number' ? t.organicScore : null,
      verified: !!(t.isVerified || t.tags?.includes?.('verified')),
    })).filter(t => t.id)
  } catch (err) {
    if (err.name === 'AbortError') return []
    console.warn('searchTokens failed:', err.message)
    return []
  }
}

export async function getTokenName(mintAddress) {
  try {
    const resp = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mintAddress}`)
    if (resp.ok) {
      const data = await resp.json()
      if (data.pairs?.[0]?.baseToken?.symbol) return data.pairs[0].baseToken.symbol
    }
  } catch {}

  if (JUP_API_KEY) {
    try {
      const res = await fetch(
        `https://api.jup.ag/tokens/v2/search?query=${mintAddress}`,
        { headers: getHeaders() }
      )
      if (res.ok) {
        const data = await res.json()
        const token = Array.isArray(data) ? data.find(t => t.id === mintAddress) : null
        if (token?.symbol) return token.symbol
      }
    } catch {}
  }

  return mintAddress.slice(0, 6) + '...'
}

// ============================================
// Entry Price Detection
// ============================================

export async function getEntryPrice(walletPubkey, tokenMint, totalSupply) {
  try {
    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
      walletPubkey,
      { mint: new PublicKey(tokenMint) }
    )
    if (tokenAccounts.value.length === 0) return null
    const tokenAccountPubkey = tokenAccounts.value[0].pubkey
    const walletAddr = walletPubkey.toBase58()

    let allSigs = []
    let before = undefined
    for (let page = 0; page < 10; page++) {
      const opts = { limit: 100 }
      if (before) opts.before = before
      const sigs = await connection.getSignaturesForAddress(tokenAccountPubkey, opts)
      if (sigs.length === 0) break
      allSigs = allSigs.concat(sigs)
      if (sigs.length < 100) break
      before = sigs[sigs.length - 1].signature
    }

    allSigs.reverse()

    for (const sigInfo of allSigs) {
      if (sigInfo.err) continue
      let tx
      try {
        tx = await connection.getParsedTransaction(sigInfo.signature, {
          commitment: 'confirmed',
          maxSupportedTransactionVersion: 0,
        })
      } catch { continue }
      if (!tx || !tx.meta) continue

      const preTokenBals = tx.meta.preTokenBalances || []
      const postTokenBals = tx.meta.postTokenBalances || []
      const mintAddr = tokenMint

      const preEntry = preTokenBals.find(b => b.mint === mintAddr && b.owner === walletAddr)
      const postEntry = postTokenBals.find(b => b.mint === mintAddr && b.owner === walletAddr)

      const preTok = preEntry ? parseFloat(preEntry.uiTokenAmount.uiAmount || 0) : 0
      const postTok = postEntry ? parseFloat(postEntry.uiTokenAmount.uiAmount || 0) : 0
      const tokensReceived = postTok - preTok
      if (tokensReceived <= 0) continue

      const accountKeys = tx.transaction.message.accountKeys
      const walletKeyIndex = accountKeys.findIndex(
        k => (k.pubkey ? k.pubkey.toBase58() : k.toString()) === walletAddr
      )
      if (walletKeyIndex === -1) continue

      const preSOL = (tx.meta.preBalances[walletKeyIndex] || 0) / 1e9
      const postSOL = (tx.meta.postBalances[walletKeyIndex] || 0) / 1e9
      const fee = (tx.meta.fee || 0) / 1e9
      const solSpent = preSOL - postSOL - fee

      if (solSpent <= 0) continue

      const pricePerToken = solSpent / tokensReceived
      const entryMcap = pricePerToken * totalSupply

      return { entryMcap, tokenAmount: tokensReceived, solSpent }
    }

    return null
  } catch (err) {
    console.error('getEntryPrice error:', err)
    return null
  }
}


// ============================================
// Jupiter v2 token search — full record (organicScore, stats24h, mcap,
// holderCount, audit, launchpad, etc). Used by the watchlist enrichment
// so cards can render rich data without round-tripping every time.
// Returns the matched token record or null on any failure.
// Uses ULTRA_BASE which picks the keyed or lite endpoint automatically.
// ============================================
export async function fetchTokenV2Data(mintAddress) {
  if (!mintAddress) return null
  try {
    const res = await fetch(
      `${ULTRA_BASE}/tokens/v2/search?query=${encodeURIComponent(mintAddress)}`,
      { headers: getHeaders() }
    )
    if (!res.ok) return null
    const data = await res.json()
    // The search endpoint returns either an array or { tokens: [...] }
    // depending on key vs lite. Handle both shapes.
    const list = Array.isArray(data) ? data : (data?.tokens || [])
    const match = list.find(t => (t.id || t.address) === mintAddress) || list[0] || null
    if (!match) return null
    // Normalize HERE, not at the call site. This record is SPREAD into the
    // token watchlist entry by the chart's enrichment pass, and that entry is
    // persisted to localStorage AND pushed to D1 by the settings sync. An
    // unnormalized icon at this door isn't a bad render once — it's written to
    // permanent storage and replicated to every other device on the account.
    // Same reasoning as searchTokens: normalize where tokens ENTER.
    return { ...match, icon: normalizeTokenImage(match.icon) }
  } catch {
    return null
  }
}


// ============================================
// Token spark OHLC — small chart data for the watchlist sparks.
// Reuses the same GeckoTerminal OHLCV endpoint the main chart already
// uses for its historical seed (see fetchHistoricalCandles in chartUtils.js)
// — the only difference is the limit and the absence of mcap conversion;
// sparks just need relative price motion.
//
// Pool address is resolved once per mint via DexScreener and cached in a
// module-level Map for the session. localStorage caching happens upstream
// in the watchlist entry itself (chart + chartFetchedAt fields), so the
// next time the user opens the watchlist, the spark renders from cache
// while a background refresh fires if data is stale.
// ============================================
const _poolCache = new Map()  // mintAddress → poolAddress

async function _resolvePool(mintAddress) {
  if (_poolCache.has(mintAddress)) return _poolCache.get(mintAddress)
  try {
    const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mintAddress}`)
    if (!res.ok) return null
    const data = await res.json()
    const pairs = (data.pairs || []).filter(p => p.chainId === 'solana')
    if (!pairs.length) return null
    // Highest-liquidity pair is the most representative — same selection
    // logic as fetchHistoricalCandles.
    const pair = pairs.sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0]
    const pool = pair.pairAddress
    if (pool) _poolCache.set(mintAddress, pool)
    return pool
  } catch {
    return null
  }
}

/**
 * Fetch a tiny OHLC series for a token's spark display.
 * @param {string} mintAddress
 * @param {object} opts
 * @param {number} opts.count          - candles to fetch (default 24)
 * @param {number} opts.aggregateMin   - candle period in minutes (default 15)
 * @returns {Promise<Array<{ts,o,h,l,c}> | null>}  chronological (oldest-first), null on failure
 */
export async function fetchTokenSparkOHLC(mintAddress, opts = {}) {
  if (!mintAddress) return null
  const count        = opts.count        ?? 24
  const aggregateMin = opts.aggregateMin ?? 15
  const pool = await _resolvePool(mintAddress)
  if (!pool) return null
  try {
    // GeckoTerminal's /minute endpoint with aggregate=N gives N-minute candles.
    // Same headers as fetchHistoricalCandles.
    // Routed through the backend HTTP proxy (port 8766) because GT doesn't
    // send CORS headers — see server_swaps.py start_http_proxy().
    const res = await fetch(
      `${HTTP_PROXY_BASE}/gt/api/v2/networks/solana/pools/${pool}/ohlcv/minute?limit=${count}&aggregate=${aggregateMin}`,
      { headers: { Accept: 'application/json;version=20230302' } }
    )
    if (!res.ok) return null
    const data = await res.json()
    const list = data.data?.attributes?.ohlcv_list || []
    if (!list.length) return null
    // GT returns newest-first as [ts, o, h, l, c, v]. Reverse for
    // chronological. Drop volume — the spark doesn't need it. Keep
    // numbers compact for localStorage.
    return list
      .map(([ts, o, h, l, c]) => ({ ts: ts * 1000, o, h, l, c }))
      .reverse()
  } catch {
    return null
  }
}

// ============================================
// 🛡️ Stop Protection (trailing stop guard)
// ============================================
// Jupiter's Trigger API is LIMIT orders only — a sell-limit placed below
// market fills immediately, so it cannot serve as a stop. True stop
// protection is therefore price-monitored: the guard watches the mcap the
// chart already streams, ratchets the stop upward (never down), and fires
// a market sell through the normal executeSell path the instant the stop
// is breached. Pre-warm keeps the sell path hot so the exit is fast.
//
// Usage:
//   armStopProtection({ tokenMint, wallet, signTransaction,
//                       stopMcap, sellPct = 100,
//                       trailPct = null,       // e.g. 0.03 → trail 3% under peak
//                       onTrigger, onRatchet })
//   updateStopGuard(currentMcap)   // call on every price tick (chart feed)
//   ratchetStop(newStopMcap)       // manual/external ratchet (e.g. AVWAP line)
//   disarmStopProtection()

let _stopGuard = null   // { tokenMint, wallet, sign, stopMcap, sellPct, trailPct, peakMcap, firing, onTrigger, onRatchet }

export function armStopProtection({ tokenMint, wallet, signTransaction, stopMcap, sellPct = 100, trailPct = null, onTrigger = null, onRatchet = null }) {
  if (!tokenMint || !wallet || !signTransaction || !Number.isFinite(stopMcap) || stopMcap <= 0) {
    throw new Error('armStopProtection: tokenMint, wallet, signTransaction, stopMcap required')
  }
  _stopGuard = {
    tokenMint, wallet, sign: signTransaction,
    stopMcap, sellPct, trailPct,
    peakMcap: 0, firing: false,
    onTrigger, onRatchet,
  }
  return { ...publicGuardState() }
}

export function ratchetStop(newStopMcap) {
  const g = _stopGuard
  if (!g || !Number.isFinite(newStopMcap)) return null
  if (newStopMcap > g.stopMcap) {          // ratchet-only-up: protection never loosens
    g.stopMcap = newStopMcap
    g.onRatchet?.(g.stopMcap)
  }
  return publicGuardState()
}

export function disarmStopProtection() { _stopGuard = null }

export function getStopGuard() { return _stopGuard ? publicGuardState() : null }

function publicGuardState() {
  const g = _stopGuard
  return { tokenMint: g.tokenMint, stopMcap: g.stopMcap, sellPct: g.sellPct, trailPct: g.trailPct, peakMcap: g.peakMcap, firing: g.firing }
}

export function updateStopGuard(currentMcap) {
  const g = _stopGuard
  if (!g || g.firing || !Number.isFinite(currentMcap) || currentMcap <= 0) return null

  // Optional percentage trail off the peak (in addition to external ratchets).
  if (g.trailPct && currentMcap > g.peakMcap) {
    g.peakMcap = currentMcap
    const trailStop = currentMcap * (1 - g.trailPct)
    if (trailStop > g.stopMcap) { g.stopMcap = trailStop; g.onRatchet?.(g.stopMcap) }
  }

  if (currentMcap <= g.stopMcap) {
    g.firing = true
    const fired = { ...publicGuardState(), triggeredAt: currentMcap, time: Date.now() }
    // Fire-and-forget market sell; guard disarms itself either way so a
    // failed send can be retried manually rather than machine-gunned.
    executeSell(g.tokenMint, g.sellPct, g.wallet, g.sign, null)
      .then((res) => { g.onTrigger?.({ ...fired, ok: true, res }) })
      .catch((err) => { g.onTrigger?.({ ...fired, ok: false, error: err?.message || String(err) }) })
      .finally(() => { if (_stopGuard === g) _stopGuard = null })
    return fired
  }
  return null
}


// ============================================
// positions-20261017: chain truth for position tags
// ============================================

// Summed balance per mint for the wallet, both token programs. THROWS on any
// RPC error so callers keep their last state (an error is never a zero).
// Returns Map(mint -> { ui, nonzero }). A mint missing from the map = 0.
export async function getWalletMintTotals(walletPubkey) {
  const [spl, t22] = await Promise.all([
    connection.getParsedTokenAccountsByOwner(walletPubkey, { programId: new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA') }),
    connection.getParsedTokenAccountsByOwner(walletPubkey, { programId: new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb') }),
  ])
  const out = new Map()
  for (const ta of [...(spl?.value || []), ...(t22?.value || [])]) {
    const info = ta?.account?.data?.parsed?.info
    if (!info || !info.mint || !info.tokenAmount) continue
    const raw = String(info.tokenAmount.amount || '0')
    const ui = parseFloat(info.tokenAmount.uiAmountString ?? info.tokenAmount.uiAmount ?? 0) || 0
    const cur = out.get(info.mint) || { ui: 0, nonzero: false }
    cur.ui += ui
    if (/[1-9]/.test(raw)) cur.nonzero = true
    out.set(info.mint, cur)
  }
  return out
}

// One parsed tx -> the wallet's fill for `mint`, or null. SOL leg = wallet
// lamports + wallet wSOL delta, minus the network fee (fee payer only), minus
// rent for a token account of this mint created in the tx (plus rent refund
// when one is closed), so the token-account creation fee never lands in entry.
function fillFromParsedTx(tx, walletAddr, mint) {
  const meta = tx.meta
  const keys = tx.transaction?.message?.accountKeys || []
  const keyStr = (k) => (k?.pubkey ? k.pubkey.toBase58() : String(k))
  const wi = keys.findIndex((k) => keyStr(k) === walletAddr)
  const pre = meta.preTokenBalances || []
  const post = meta.postTokenBalances || []
  const amt = (b) => parseFloat(b?.uiTokenAmount?.uiAmountString ?? b?.uiTokenAmount?.uiAmount ?? 0) || 0
  let preTok = 0, postTok = 0, wsolOut = 0
  for (const b of pre) {
    if (b.owner !== walletAddr) continue
    if (b.mint === mint) preTok += amt(b)
    else if (b.mint === SOL_MINT) wsolOut += amt(b)
  }
  for (const b of post) {
    if (b.owner !== walletAddr) continue
    if (b.mint === mint) postTok += amt(b)
    else if (b.mint === SOL_MINT) wsolOut -= amt(b)
  }
  const tok = postTok - preTok
  if (!(Math.abs(tok) > 0)) return null
  const preB = meta.preBalances || [], postB = meta.postBalances || []
  let solSpent = wsolOut
  if (wi >= 0) {
    solSpent += ((preB[wi] || 0) - (postB[wi] || 0)) / 1e9
    if (wi === 0) solSpent -= (meta.fee || 0) / 1e9
  }
  const idxs = new Set()
  for (const b of [...pre, ...post]) if (b.owner === walletAddr && b.mint === mint) idxs.add(b.accountIndex)
  for (const i of idxs) {
    const a = preB[i] || 0, z = postB[i] || 0
    if (a === 0 && z > 0) solSpent -= z / 1e9        // account created: rent paid
    else if (a > 0 && z === 0) solSpent += a / 1e9   // account closed: rent refunded
  }
  return tok > 0
    ? { side: 'buy', tok, sol: Math.max(0, solSpent), preTok }
    : { side: 'sell', tok: -tok, sol: Math.max(0, -solSpent), preTok }
}

// The wallet's fills for `mint` since it was last flat, oldest first. Walks
// signatures newest -> oldest and stops at the buy that started from zero.
// THROWS on RPC errors. sig is the first 16 chars (matches wallet_activity).
export async function getMintFillsSinceFlat(walletPubkey, mint, maxTx = 150) {
  const walletAddr = walletPubkey.toBase58()
  const accts = await connection.getParsedTokenAccountsByOwner(walletPubkey, { mint: new PublicKey(mint) })
  const accKeys = (accts?.value || []).map((a) => a.pubkey)
  if (!accKeys.length) return { fills: [], foundFlat: false, scanned: 0 }
  const sigMap = new Map()
  for (const pk of accKeys) {
    let before
    for (let page = 0; page < 3; page++) {
      const opts = { limit: 100 }
      if (before) opts.before = before
      const sigs = await connection.getSignaturesForAddress(pk, opts)
      for (const s of sigs) if (!sigMap.has(s.signature)) sigMap.set(s.signature, s)
      if (sigs.length < 100) break
      before = sigs[sigs.length - 1].signature
    }
  }
  const sigs = [...sigMap.values()].filter((s) => !s.err).sort((a, b) => (b.slot || 0) - (a.slot || 0))
  const back = []
  let foundFlat = false, scanned = 0
  for (const s of sigs) {
    if (scanned >= maxTx) break
    scanned++
    const tx = await connection.getParsedTransaction(s.signature, { commitment: 'confirmed', maxSupportedTransactionVersion: 0 })
    if (!tx || !tx.meta || tx.meta.err) continue
    const f = fillFromParsedTx(tx, walletAddr, mint)
    if (!f) continue
    back.push({ sig: s.signature.slice(0, 16), ts: (tx.blockTime || s.blockTime || 0) * 1000, side: f.side, tok: f.tok, sol: f.sol })
    if (f.side === 'buy' && f.preTok <= 0) { foundFlat = true; break }
  }
  return { fills: back.reverse(), foundFlat, scanned }
}